// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import React, { type ComponentType, type PropsWithChildren, useCallback } from 'react';

import { ErrorBoundary as CommonErrorBoundary } from '@atlassian/search-common/error-boundary';

import type { ErrorComponentProps } from '../../common/ui/error';
import { onErrorBoundaryHit } from '../../common/utils/events/error-boundary-hit';
import { useAnalytics, useCommonAndPrivateAttributes } from '../analytics';

import { useLogException } from './utils';

// re-exporting for backwards compatibility
export { useLogException } from './utils';
export { logException, type onError } from './log-exception';

type VoidFunc = () => void;

type ErrorBoundaryProps = {
	fallback?: React.ReactNode;
	renderWithErrorInfo?: ComponentType<ErrorComponentProps>;
	section: string;
	onException?: (error: Error) => void;
	setErrorBoundaryResetCb?: (cb?: VoidFunc) => void;
};

export const ErrorBoundary = ({
	children,
	fallback,
	renderWithErrorInfo,
	section,
	onException: customOnException,
	setErrorBoundaryResetCb,
}: PropsWithChildren<ErrorBoundaryProps>): React.JSX.Element => {
	const logException = useLogException();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const onException = useCallback(
		(error: any) => {
			fireAnalyticsEvent(
				onErrorBoundaryHit({
					commonAttributes,
					nonPrivacySafeAttributes,
					failSection: section,
				}),
			);
			logException(error);

			// Call custom handler if provided
			customOnException?.(error);
		},
		[
			logException,
			fireAnalyticsEvent,
			commonAttributes,
			nonPrivacySafeAttributes,
			section,
			customOnException,
		],
	);

	return (
		<CommonErrorBoundary
			fallback={fallback}
			renderWithErrorInfo={renderWithErrorInfo}
			onException={onException}
			setErrorBoundaryResetCb={setErrorBoundaryResetCb}
		>
			{children}
		</CommonErrorBoundary>
	);
};
